/**
 * Replay API Service
 * Frontend client for game replay endpoints
 */

import apiClient from './apiClient';

export interface GameStateSnapshot {
  timestamp: Date;
  roundNumber: number;
  currentPlayerIndex: number;
  currentPhase: string;
  players: Array<{
    userId: string;
    username: string;
    hand: string[];
    tricksWon: number;
    score: number;
  }>;
  currentTrick: Array<{
    playerId: string;
    card: string;
  }>;
  playedCards: string[];
  trumpSuit?: string;
  biddingInfo?: {
    bids: Array<{
      playerId: string;
      bid: number | 'pass';
    }>;
    currentBidder: string;
  };
}

export interface GameReplay {
  _id: string;
  gameId: string;
  roomId: string;
  gameType: string;
  playerIds: string[];
  playerNames: string[];
  startedAt: Date;
  completedAt: Date;
  duration: number;
  snapshots: GameStateSnapshot[];
  finalResult: {
    winner: string;
    scores: Array<{
      userId: string;
      score: number;
      tricksWon: number;
    }>;
  };
  metadata: {
    recordedBy: string;
    snapshotCount: number;
    avgSnapshotInterval: number;
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface ReplayListItem {
  _id: string;
  gameId: string;
  roomId: string;
  gameType: string;
  playerNames: string[];
  completedAt: Date;
  duration: number;
  finalResult: {
    winner: string;
  };
  metadata: {
    snapshotCount: number;
  };
}

class ReplayApi {
  /**
   * Get replay by game ID (full with snapshots)
   */
  async getReplay(gameId: string): Promise<GameReplay> {
    try {
      const response = await apiClient.get(`/replays/${gameId}`);
      return response.data.data;
    } catch (error) {
      console.error('[replayApi] Error fetching replay:', error);
      throw error;
    }
  }

  /**
   * Get recent replays for a room
   */
  async getRoomReplays(roomId: string, limit: number = 10): Promise<ReplayListItem[]> {
    try {
      const response = await apiClient.get(`/replays/room/${roomId}?limit=${limit}`);
      return response.data.data;
    } catch (error) {
      console.error('[replayApi] Error fetching room replays:', error);
      throw error;
    }
  }

  /**
   * Get replays for a player
   */
  async getPlayerReplays(userId: string, limit: number = 10): Promise<ReplayListItem[]> {
    try {
      const response = await apiClient.get(`/replays/player/${userId}?limit=${limit}`);
      return response.data.data;
    } catch (error) {
      console.error('[replayApi] Error fetching player replays:', error);
      throw error;
    }
  }

  /**
   * Format duration from milliseconds to readable string
   */
  formatDuration(ms: number): string {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  }

  /**
   * Get formatted timestamp
   */
  formatTimestamp(date: Date | string): string {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}

export const replayApi = new ReplayApi();
